import React from 'react';
import { useSelector } from 'react-redux';
import { Ad,
  AppContent,
  Author,
  ErrorBoundary,
  Section,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { LoadMoreButton, useFetchNextPosts } from '../../components/connectedComponents/LoadMoreButton';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { getSecondaryHeader } from '../../store/navigations/ampNavigation/secondaryHeader/secondaryHeader.selectors';
import {
  getArticles,
  getBio,
  getImage,
  getJoinDate,
  getJoinDateText,
  getMetadataTitle,
  getMetadataDescription,
  getMetadataImage,
  getName,
  getShowMorePaginationText,
  getShowMorePaginationURLByKey,
  getSocialButtons,
} from '../../store/template/authorPage/authorPageAntalya/authorPageAntalya.selectors';
import { AuthorPageSectionName } from '../../store/template/authorPage/authorPage.reducer';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import {
  getAdSlotId,
  getAdSlotOnSuccess,
  getCanonicalUrl,
  getEdition,
  getUseFormatMinutesHoursAgo,
} from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';

interface AuthorPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const AuthorPageAntalyaComponent: React.FunctionComponent<AuthorPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <AuthorPageContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    justifyItems: 'center',
  },
});

const createTwitterAndOgTitleForSI = (edition: string, name: string) => {
  if (edition === 'si_lifestyle') return `Articles by ${name} - Sports Illustrated Lifestyle`;
  if (edition === 'si_swimsuit') return `Articles by ${name} - Swimsuit | SI.com`;
  return '';
};

export const AuthorPageContentConnected: React.FunctionComponent = () => {
  const image = useSelector(getImage);
  const name = useSelector(getName);
  const socialButtons = useSelector(getSocialButtons);
  const bio = useSelector(getBio);
  const joinDateText = useSelector(getJoinDateText);
  const joinDate = useSelector(getJoinDate);
  const articles = useSelector(getArticles);
  const isSecondaryHeaderEnabled = !!useSelector(getSecondaryHeader);
  const stickyBottomId = useSelector(getAdSlotId(SLOT_NAMES.STICKY_BOTTOM));
  const stickyBottomOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.STICKY_BOTTOM));
  const style = createStyle();
  const edition = useSelector(getEdition);
  const useFormatMinutesHoursAgo = useSelector(getUseFormatMinutesHoursAgo);
  const metaTitle = useSelector(getMetadataTitle);
  const metaDescription = useSelector(getMetadataDescription);
  const pageImage = useSelector(getMetadataImage);
  const canonical = useSelector(getCanonicalUrl);
  const isSiOrFannation = canonical.includes('www.si');
  const siTwitterAndOgTitle = createTwitterAndOgTitleForSI(edition, name);

  const sanitizedTitle = () => {
    if (edition === 'si_lifestyle' || edition === 'si_swimsuit') {
      return (metaTitle || name || '').replace(' | SI.com', '');
    }

    if (isSiOrFannation) {
      return `${name} - Sports Illustrated`;
    }

    return name;
  };

  const sanitizedDescription = () => {
    if (!isSiOrFannation && edition !== 'si_lifestyle' && edition !== 'si_swimsuit') {
      return bio;
    }

    const defaultAuthorBio = `Articles by ${name}, writer at Sports Illustrated`;
    const description = (metaDescription || bio || defaultAuthorBio || '').replace(' | SI.com', '');

    return description.length >= 160 ? `${description.substring(0, 157)}...` : description || '';
  };

  return (
    <React.Fragment>
      <MetaTags articles={articles} description={sanitizedDescription()} title={sanitizedTitle()} pageImage={pageImage} siTwitterAndOgTitle={siTwitterAndOgTitle} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <NavigationLayout mainWrapperConfigThemeKey="mainContent">
            <div className={css(style.sectionsWrapper)}>
              <Section>
                <Author {...{
                  image,
                  name,
                  socialButtons,
                  bio,
                  joinDateText,
                  joinDate,
                  articles,
                  isSecondaryHeaderEnabled,
                  useFormatMinutesHoursAgo,
                }}
                />
              </Section>
            </div>
            <LoadMoreAuthorPostsButtonConnected />
          </NavigationLayout>
          <Ad id={stickyBottomId} onSuccess={stickyBottomOnSuccess} isSticky />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};


export const LoadMoreAuthorPostsButtonConnected: React.FunctionComponent = () => {
  const shouldDisplay = !!useSelector(getShowMorePaginationURLByKey(AuthorPageSectionName));
  const text = useSelector(getShowMorePaginationText);
  const onClick = useFetchNextPosts(AuthorPageSectionName, getShowMorePaginationURLByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
